import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  UserPlus,
  Eye,
  EyeOff,
  MousePointer,
  AlertTriangle,
  Crown,
  ShieldAlert,
  AtSign,
  Archive,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../styles/theme";
import { Badge, Button, SearchBar, Table } from "../components/ui";
import { ContactDrawer } from "../components/ContactDrawer";
import { AddContactModal } from "../components/AddContactModal";
import {
  fetchContacts,
  fetchContact,
  setDecisionMaker,
  updateContact,
  bulkUpdateContacts,
  mergeContacts,
  deriveContactType,
  replaceTypeTag,
  getAllTypeTags,
  getCanonicalTagForType,
  displayName,
  initials,
  fmtDate,
} from "../services/api";

const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "hotmail.com",
  "hotmail.co.uk", "outlook.com", "live.com", "live.co.uk", "msn.com",
  "aol.com", "icloud.com", "me.com", "mac.com", "protonmail.com",
  "proton.me", "zoho.com", "mail.com", "gmx.com", "yandex.com",
  "tutanota.com", "fastmail.com", "hey.com", "pm.me",
]);

function isFreeEmail(email) {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? FREE_EMAIL_DOMAINS.has(domain) : false;
}

export default function Contacts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [inlineTypeFor, setInlineTypeFor] = useState(null); // contact id for inline type dropdown
  const [confirmTypeChange, setConfirmTypeChange] = useState(null); // { contactId, contactName, newTypeId, oldTypeInfo }
  const [bulkTypeOpen, setBulkTypeOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { typeId }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null); // "archive" | "unarchive" | null
  const [archiveSaving, setArchiveSaving] = useState(false);
  const [hideInactive, setHideInactive] = useState(true);
  const [mergeSaving, setMergeSaving] = useState(false);

  const showArchived = filterType === "archived";
  const urlSort = searchParams.get("sort"); // e.g. "createdAt"
  // Map API sort field to Table column key
  const sortKeyMap = { createdAt: "created" };
  const tableSortKey = urlSort ? (sortKeyMap[urlSort] || urlSort) : null;

  // Deep-link: auto-open contact from ?contact=ID
  useEffect(() => {
    const contactId = searchParams.get("contact");
    if (contactId && !selectedContact) {
      fetchContact(contactId)
        .then((c) => setSelectedContact(c))
        .catch(() => {});
      // Clear the param so it doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const handleToggleDecisionMaker = async (e, contact) => {
    e.stopPropagation();
    const newVal = !contact.isDecisionMaker;
    setContacts((prev) =>
      prev.map((c) =>
        c.id === contact.id ? { ...c, isDecisionMaker: newVal } : c,
      ),
    );
    try {
      await setDecisionMaker(contact.id, contact.email, newVal);
    } catch {
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contact.id ? { ...c, isDecisionMaker: !newVal } : c,
        ),
      );
    }
  };

  const handleInlineTypeSelect = async (contact, newTypeId) => {
    const currentType = deriveContactType(contact.tags, contact.ghlTags);
    if (newTypeId === currentType) {
      setInlineTypeFor(null);
      return;
    }
    const newInfo = CONTACT_TYPES[newTypeId];
    setConfirmTypeChange({
      contact,
      newTypeId,
      newLabel: newInfo?.label || newTypeId,
    });
    setInlineTypeFor(null);
  };

  const handleConfirmInlineType = async () => {
    if (!confirmTypeChange) return;
    const { contact, newTypeId } = confirmTypeChange;
    try {
      const newTags = replaceTypeTag(contact.tags, newTypeId);
      const updated = await updateContact(contact.id, { email: contact.email, tags: newTags });
      setContacts((prev) =>
        prev.map((c) => (c.id === contact.id ? { ...c, tags: newTags } : c)),
      );
    } catch (err) {
      alert(`Failed to change type: ${err.message}`);
    } finally {
      setConfirmTypeChange(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((c) => c.id)));
    }
  };

  const handleBulkTypeConfirm = async () => {
    if (!bulkConfirm) return;
    setBulkSaving(true);
    try {
      const typeTags = getAllTypeTags();
      const contactIds = filtered
        .filter((c) => selectedIds.has(c.id))
        .map((c) => ({ id: c.id, email: c.email }));
      const newTag = getCanonicalTagForType(bulkConfirm.typeId);
      await bulkUpdateContacts(
        contactIds,
        {},
        [newTag],
        typeTags.filter((t) => t !== newTag),
      );
      // Refresh contacts
      const data = await fetchContacts({ search, limit: 200 });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      alert(`Bulk type change failed: ${err.message}`);
    } finally {
      setBulkSaving(false);
      setBulkConfirm(null);
      setBulkTypeOpen(false);
    }
  };

  const handleBulkArchive = async () => {
    setArchiveSaving(true);
    try {
      const contactIds = filtered
        .filter((c) => selectedIds.has(c.id))
        .map((c) => ({ id: c.id, email: c.email }));
      await bulkUpdateContacts(contactIds, {}, ["archived"], []);
      const data = await fetchContacts({ search, limit: 200, includeArchived: showArchived });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      alert(`Archive failed: ${err.message}`);
    } finally {
      setArchiveSaving(false);
      setArchiveConfirm(null);
    }
  };

  const handleBulkUnarchive = async () => {
    setArchiveSaving(true);
    try {
      const contactIds = filtered
        .filter((c) => selectedIds.has(c.id))
        .map((c) => ({ id: c.id, email: c.email }));
      await bulkUpdateContacts(contactIds, {}, [], ["archived"]);
      const data = await fetchContacts({ search, limit: 200, includeArchived: showArchived });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      alert(`Unarchive failed: ${err.message}`);
    } finally {
      setArchiveSaving(false);
      setArchiveConfirm(null);
    }
  };

  const handleMergeSelected = async () => {
    if (selectedIds.size !== 2) return;
    const [id1, id2] = [...selectedIds];
    const c1 = contacts.find((c) => c.id === id1);
    const c2 = contacts.find((c) => c.id === id2);
    if (!c1 || !c2) return;
    const name1 = displayName(c1) || c1.email;
    const name2 = displayName(c2) || c2.email;
    const ok = window.confirm(
      `Merge "${name1}" into "${name2}"?\n\n"${name2}" will be kept as the primary contact. "${name1}" will be deleted and its data merged in.`
    );
    if (!ok) return;
    setMergeSaving(true);
    try {
      await mergeContacts(id1, id2);
      const data = await fetchContacts({ search, limit: 200, includeArchived: showArchived, sort: urlSort || "createdAt" });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
    } catch (err) {
      alert(`Merge failed: ${err.message}`);
    } finally {
      setMergeSaving(false);
    }
  };

  // Fetch contacts from API (debounced for search)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timer = setTimeout(
      () => {
        fetchContacts({ search, limit: 200, includeArchived: showArchived, sort: urlSort || "createdAt" })
          .then((data) => {
            if (cancelled) return;
            setContacts(data.contacts || []);
            setTotal(data.total || 0);
          })
          .catch((err) => {
            if (cancelled) return;
            setError(err.message);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      search ? 300 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, showArchived, urlSort]);

  const INACTIVE_TAGS = useMemo(() => new Set(["inactive", "unsubscribed from newsletter", "pending unsub"]), []);

  const isInactiveContact = (c) => {
    const allTags = [...(c.tags || []), ...(c.ghlTags || [])].map((t) => t.toLowerCase());
    if (allTags.some((t) => INACTIVE_TAGS.has(t))) return true;
    const cf = c.customFields || {};
    const sent = Number(cf.emailsSentCount) || 0;
    if (sent === 0) return false; // never emailed — not inactive
    const opens = Number(cf.emailOpensCount) || 0;
    const clicks = Number(cf.emailClicksCount) || 0;
    return opens === 0 && clicks === 0;
  };

  // Client-side type filter (type is derived from tags)
  const filtered = useMemo(() => {
    return contacts.filter((c) => {
      if (showArchived) {
        return (c.tags || []).some((t) => t.toLowerCase() === "archived");
      }
      if (hideInactive && isInactiveContact(c)) return false;
      if (filterType === "all") return true;
      if (filterType === "decision_maker") return c.isDecisionMaker;
      if (filterType === "personal_email") return isFreeEmail(c.email);
      if (filterType === "bounced")
        return (
          (Number(c.customFields?.emailsBouncedCount) || 0) > 0 ||
          c.bounceStatus === "hard" ||
          c.spamReported
        );
      if (["hot", "warm", "cool", "cold"].includes(filterType))
        return c.engagementTier === filterType;
      return deriveContactType(c.tags, c.ghlTags) === filterType;
    });
  }, [contacts, filterType, hideInactive]);

  const contactColumns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={filtered.length > 0 && selectedIds.size === filtered.length}
          onChange={(e) => { e.stopPropagation(); toggleSelectAll(); }}
          style={{ cursor: "pointer", accentColor: colors.accent }}
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={(e) => { e.stopPropagation(); toggleSelect(row.id); }}
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: "pointer", accentColor: colors.accent }}
        />
      ),
    },
    {
      key: "name",
      label: "Contact",
      sortValue: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim().toLowerCase(),
      render: (row) => {
        const type = deriveContactType(row.tags, row.ghlTags);
        const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
        const bounced = Number(row.customFields?.emailsBouncedCount) || 0;
        const hardBounce = row.bounceStatus === "hard";
        const spam = row.spamReported;
        const hasProblem = bounced > 0 || hardBounce || spam;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: hasProblem ? "rgba(239,68,68,0.1)" : typeInfo.bg,
                color: hasProblem ? colors.danger : typeInfo.color,
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              {spam ? (
                <ShieldAlert size={14} />
              ) : hasProblem ? (
                <AlertTriangle size={14} />
              ) : (
                initials(row)
              )}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600 }}>{displayName(row)}</span>
                {row.isDecisionMaker && (
                  <Crown
                    size={12}
                    color="#f59e0b"
                    fill="#f59e0b"
                    style={{ flexShrink: 0 }}
                  />
                )}
                {hardBounce && (
                  <span
                    title={row.lastBounceReason || "Hard bounce"}
                    style={{
                      fontSize: 10,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: "rgba(239,68,68,0.1)",
                      color: colors.danger,
                      fontWeight: 600,
                    }}
                  >
                    Hard bounce
                  </span>
                )}
                {spam && (
                  <span
                    title="Reported as spam"
                    style={{
                      fontSize: 10,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: "rgba(239,68,68,0.1)",
                      color: colors.danger,
                      fontWeight: 600,
                    }}
                  >
                    Spam
                  </span>
                )}
                {bounced > 0 && !hardBounce && (
                  <span
                    title={`${bounced} bounced email${bounced > 1 ? "s" : ""}`}
                    style={{
                      fontSize: 10,
                      padding: "1px 5px",
                      borderRadius: 3,
                      background: "rgba(239,68,68,0.1)",
                      color: colors.danger,
                      fontWeight: 600,
                    }}
                  >
                    {bounced} bounce{bounced > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {row.email}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: "company",
      label: "Company",
      sortValue: (row) => (row.company || "").toLowerCase(),
      render: (row) => (
        <span style={{ color: colors.textSecondary }}>
          {row.company || "—"}
        </span>
      ),
    },
    {
      key: "dm",
      label: "DM",
      render: (row) => (
        <button
          onClick={(e) => handleToggleDecisionMaker(e, row)}
          title={
            row.isDecisionMaker
              ? "Remove as decision maker"
              : "Set as decision maker"
          }
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            background: row.isDecisionMaker
              ? "rgba(245,158,11,0.15)"
              : "transparent",
            transition: "all 0.15s",
          }}
        >
          <Crown
            size={14}
            color={row.isDecisionMaker ? "#f59e0b" : colors.border}
            fill={row.isDecisionMaker ? "#f59e0b" : "none"}
          />
        </button>
      ),
    },
    {
      key: "type",
      label: "Type",
      sortValue: (row) => (CONTACT_TYPES[deriveContactType(row.tags, row.ghlTags)]?.label || "subscriber").toLowerCase(),
      render: (row) => {
        const type = deriveContactType(row.tags, row.ghlTags);
        const typeInfo = CONTACT_TYPES[type] || CONTACT_TYPES.subscriber;
        const isOpen = inlineTypeFor === row.id;
        return (
          <div style={{ position: "relative" }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setInlineTypeFor(isOpen ? null : row.id);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${typeInfo.color}44`,
                background: typeInfo.bg,
                color: typeInfo.color,
                fontSize: 11,
                fontFamily: fonts.body,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {typeInfo.label}
              <span style={{ fontSize: 8, opacity: 0.5 }}>{isOpen ? "\u25B2" : "\u25BC"}</span>
            </button>
            {isOpen && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: 4,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  zIndex: 20,
                  padding: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  maxWidth: 360,
                }}
              >
                {Object.entries(CONTACT_TYPES).map(([id, info]) => (
                  <button
                    key={id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInlineTypeSelect(row, id);
                    }}
                    style={{
                      padding: "3px 8px",
                      borderRadius: 4,
                      border: `1px solid ${id === type ? info.color : colors.border}`,
                      background: id === type ? info.bg : "transparent",
                      color: id === type ? info.color : colors.textSecondary,
                      fontSize: 10,
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {info.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "engagement",
      label: "Engagement",
      sortValue: (row) => (Number(row.customFields?.emailOpensCount) || 0) + (Number(row.customFields?.emailClicksCount) || 0),
      render: (row) => {
        const cf = row.customFields || {};
        const opens = Number(cf.emailOpensCount) || 0;
        const clicks = Number(cf.emailClicksCount) || 0;
        const rate = cf.openRate ? `${Number(cf.openRate).toFixed(0)}%` : null;
        if (!opens && !clicks)
          return <span style={{ color: colors.textSecondary }}>—</span>;
        return (
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span
              title="Opens"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                fontSize: 12,
                fontFamily: fonts.mono,
                color: opens > 0 ? colors.success : colors.textSecondary,
              }}
            >
              <Eye size={11} />
              {opens}
            </span>
            <span
              title="Clicks"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                fontSize: 12,
                fontFamily: fonts.mono,
                color: clicks > 0 ? colors.accent : colors.textSecondary,
              }}
            >
              <MousePointer size={11} />
              {clicks}
            </span>
            {rate && (
              <span style={{ fontSize: 10, color: colors.textSecondary }}>
                {rate}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "score",
      label: "Score",
      sortValue: (row) => row.engagementScore ?? -1,
      render: (row) => {
        const score = row.engagementScore;
        const tier = row.engagementTier;
        if (score == null)
          return <span style={{ color: colors.textSecondary }}>—</span>;
        const tierColors = {
          hot: { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
          warm: { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
          cool: { color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
          cold: { color: "#9ca3b4", bg: "rgba(156,163,180,0.12)" },
        };
        const tc = tierColors[tier] || tierColors.cold;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: tc.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: fonts.mono,
                fontSize: 12,
                fontWeight: 700,
                color: tc.color,
              }}
            >
              {score}
            </span>
            <span
              style={{
                fontSize: 10,
                padding: "1px 5px",
                borderRadius: 3,
                background: tc.bg,
                color: tc.color,
                fontWeight: 600,
                textTransform: "capitalize",
              }}
            >
              {tier}
            </span>
          </div>
        );
      },
    },
    {
      key: "created",
      label: "Created",
      sortValue: (row) => row.createdAt ? new Date(row.createdAt).getTime() : 0,
      render: (row) => (
        <span style={{ fontSize: 12, fontFamily: fonts.mono, color: colors.textSecondary }}>
          {row.createdAt ? fmtDate(row.createdAt) : "—"}
        </span>
      ),
    },
    {
      key: "tags",
      label: "Tags",
      render: (row) => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {(row.tags || []).slice(0, 2).map((t) => (
            <span
              key={t}
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 3,
                fontFamily: fonts.mono,
                background: "rgba(0,0,0,0.04)",
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "updatedAt",
      label: "Last Updated",
      sortValue: (row) => row.updatedAt ? new Date(row.updatedAt).getTime() : 0,
      render: (row) => {
        const date = row.updatedAt;
        return (
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: 12,
              color: colors.textSecondary,
            }}
          >
            {date ? fmtDate(date) : "—"}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Contacts
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {loading
              ? "Loading..."
              : `${filtered.length} of ${total.toLocaleString()} contacts`}
          </p>
        </div>
        <Button
          variant="primary"
          icon={UserPlus}
          onClick={() => setShowAddModal(true)}
        >
          Add Contact
        </Button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search contacts..."
        />
        <button
          onClick={() => setHideInactive(!hideInactive)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 6,
            border: `1px solid ${hideInactive ? colors.accent : colors.border}`,
            background: hideInactive ? colors.accentDim : "transparent",
            color: hideInactive ? colors.accent : colors.textSecondary,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: fonts.body,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "all 0.15s",
          }}
        >
          {hideInactive ? <EyeOff size={14} /> : <Eye size={14} />}
          {hideInactive ? "Show inactive" : "Hide inactive"}
        </button>
      </div>

      {/* Filter rows */}
      {(() => {
        const filterBtn = (f) => (
          <button
            key={f.id}
            onClick={() => setFilterType(f.id)}
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              border: `1px solid ${filterType === f.id ? (f.danger ? colors.danger : f.accent ? "#f59e0b" : f.tierColor || colors.accent) : colors.border}`,
              background:
                filterType === f.id
                  ? f.danger
                    ? "rgba(239,68,68,0.1)"
                    : f.accent
                      ? "rgba(245,158,11,0.1)"
                      : f.tierColor
                        ? f.tierColor + "1a"
                        : colors.accentDim
                  : "transparent",
              color:
                filterType === f.id
                  ? f.danger
                    ? colors.danger
                    : f.accent
                      ? "#f59e0b"
                      : f.tierColor || colors.accent
                  : colors.textSecondary,
              fontSize: 11,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {f.label}
          </button>
        );

        const rowStyle = { display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" };
        const labelStyle = { fontSize: 10, fontWeight: 700, color: colors.textSecondary, fontFamily: fonts.body, textTransform: "uppercase", letterSpacing: "0.05em", minWidth: 70, flexShrink: 0 };

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            <div style={rowStyle}>
              <span style={labelStyle}>Type</span>
              {filterBtn({ id: "all", label: "All" })}
              {Object.entries(CONTACT_TYPES).map(([id, v]) => filterBtn({ id, label: v.label }))}
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>Engagement</span>
              {[
                { id: "hot", label: "Hot", tierColor: "#10b981" },
                { id: "warm", label: "Warm", tierColor: "#f59e0b" },
                { id: "cool", label: "Cool", tierColor: "#6366f1" },
                { id: "cold", label: "Cold", tierColor: "#9ca3b4" },
              ].map(filterBtn)}
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>Other</span>
              {[
                { id: "decision_maker", label: "Decision Makers", accent: true },
                { id: "personal_email", label: "Personal Email", accent: true },
                { id: "bounced", label: "Bounced", danger: true },
                { id: "archived", label: "Archived" },
              ].map(filterBtn)}
            </div>
          </div>
        );
      })()}

      {error && (
        <div
          style={{
            padding: 16,
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8,
            marginBottom: 16,
            color: "#ef4444",
            fontSize: 13,
          }}
        >
          Failed to load contacts: {error}
        </div>
      )}

      <Table
        columns={contactColumns}
        data={filtered}
        onRowClick={setSelectedContact}
        initialSortKey={tableSortKey || "created"}
        initialSortDir="desc"
      />

      <ContactDrawer
        contact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onContactUpdate={(updated) => {
          setSelectedContact(updated);
          setContacts((prev) =>
            prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
          );
        }}
      />

      {showAddModal && (
        <AddContactModal
          onClose={() => setShowAddModal(false)}
          onCreated={(contact) => {
            setContacts((prev) => [contact, ...prev]);
            setTotal((prev) => prev + 1);
            setShowAddModal(false);
          }}
        />
      )}

      {/* Inline type change confirmation dialog */}
      {confirmTypeChange && (
        <>
          <div
            onClick={() => setConfirmTypeChange(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 99,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 24,
              zIndex: 100,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              minWidth: 320,
            }}
          >
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 16,
                color: colors.text,
                marginBottom: 12,
              }}
            >
              Change Contact Type
            </h3>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
              Change <strong>{displayName(confirmTypeChange.contact)}</strong> to{" "}
              <Badge
                color={CONTACT_TYPES[confirmTypeChange.newTypeId]?.color}
                bg={CONTACT_TYPES[confirmTypeChange.newTypeId]?.bg}
              >
                {confirmTypeChange.newLabel}
              </Badge>?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="default" onClick={() => setConfirmTypeChange(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmInlineType}>
                Confirm
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            zIndex: 50,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
            {selectedIds.size} selected
          </span>

          <div style={{ position: "relative" }}>
            <Button
              variant="primary"
              onClick={() => setBulkTypeOpen(!bulkTypeOpen)}
              style={{ fontSize: 12 }}
            >
              Change Type {bulkTypeOpen ? "\u25B2" : "\u25BC"}
            </Button>
            {bulkTypeOpen && !bulkConfirm && (
              <div
                style={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  marginBottom: 4,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  padding: 6,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  maxWidth: 400,
                }}
              >
                {Object.entries(CONTACT_TYPES).map(([id, info]) => (
                  <button
                    key={id}
                    onClick={() => setBulkConfirm({ typeId: id })}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 5,
                      border: `1px solid ${colors.border}`,
                      background: "transparent",
                      color: info.color,
                      fontSize: 11,
                      fontFamily: fonts.body,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {info.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showArchived ? (
            <Button
              variant="primary"
              icon={Archive}
              onClick={() => setArchiveConfirm("unarchive")}
              style={{ fontSize: 12 }}
            >
              Unarchive
            </Button>
          ) : (
            <Button
              variant="danger"
              icon={Archive}
              onClick={() => setArchiveConfirm("archive")}
              style={{ fontSize: 12 }}
            >
              Archive
            </Button>
          )}

          {selectedIds.size === 2 && (
            <Button
              variant="primary"
              onClick={handleMergeSelected}
              disabled={mergeSaving}
              style={{ fontSize: 12 }}
            >
              {mergeSaving ? "Merging…" : "Merge"}
            </Button>
          )}

          <button
            onClick={() => setSelectedIds(new Set())}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Bulk type change confirmation */}
      {bulkConfirm && (
        <>
          <div
            onClick={() => { setBulkConfirm(null); setBulkTypeOpen(false); }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 99,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 24,
              zIndex: 100,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              minWidth: 320,
            }}
          >
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 16,
                color: colors.text,
                marginBottom: 12,
              }}
            >
              Bulk Type Change
            </h3>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
              Change <strong>{selectedIds.size}</strong> contact{selectedIds.size > 1 ? "s" : ""} to{" "}
              <Badge
                color={CONTACT_TYPES[bulkConfirm.typeId]?.color}
                bg={CONTACT_TYPES[bulkConfirm.typeId]?.bg}
              >
                {CONTACT_TYPES[bulkConfirm.typeId]?.label}
              </Badge>?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button
                variant="default"
                onClick={() => { setBulkConfirm(null); setBulkTypeOpen(false); }}
                disabled={bulkSaving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkTypeConfirm}
                disabled={bulkSaving}
              >
                {bulkSaving ? "Saving..." : "Confirm"}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Archive/Unarchive confirmation */}
      {archiveConfirm && (
        <>
          <div
            onClick={() => setArchiveConfirm(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 99,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 24,
              zIndex: 100,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              minWidth: 320,
            }}
          >
            <h3
              style={{
                fontFamily: fonts.heading,
                fontSize: 16,
                color: colors.text,
                marginBottom: 12,
              }}
            >
              {archiveConfirm === "archive" ? "Archive Contacts" : "Unarchive Contacts"}
            </h3>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20 }}>
              {archiveConfirm === "archive"
                ? <><strong>{selectedIds.size}</strong> contact{selectedIds.size > 1 ? "s" : ""} will be archived and hidden from the default view.</>
                : <><strong>{selectedIds.size}</strong> contact{selectedIds.size > 1 ? "s" : ""} will be restored to the main contacts list.</>}
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button variant="default" onClick={() => setArchiveConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant={archiveConfirm === "archive" ? "danger" : "primary"}
                onClick={archiveConfirm === "archive" ? handleBulkArchive : handleBulkUnarchive}
              >
                {archiveSaving
                  ? (archiveConfirm === "archive" ? "Archiving..." : "Unarchiving...")
                  : "Confirm"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
